
import React, { useState, useContext } from 'react'
import {
	Alert,
	Button,
	Card,
	CardBody,
	Col,
	Form,
	FormGroup,
	Input,
	Label,
	Modal,
	Row
} from 'reactstrap'
import { useTranslation } from 'react-i18next'
import { SpinnerComponent } from 'react-element-spinner'

import { trigger } from '../../events/customEvents'
import AuctionTabsService from '../../services/AuctionTabsService'
import SiteService from '../../services/SiteService'
import { SessionContext } from '../../contexts/SessionProvider'

/**
 * Iteration types for relisting.
 * BRD: "Relist – 2 Pop ups, this is the ability to relist a unit into a different iteration.
 * For example, if a unit is currently in IT3 but we want to relist into IT1 (MBP)
 * we can update the iteration and it will be available in the updated iteration when
 * the auction restarts – 5PM"
 * 
 * BRD Screenshot shows: MBP, IT2, IT3, IT4 as button options
 */
const ITERATION_OPTIONS = [
	{ id: 1, label: 'MBP', fullLabel: 'MBP (Market Based Price)' },
	{ id: 2, label: 'IT2', fullLabel: 'Iteration 2' },
	{ id: 3, label: 'IT3', fullLabel: 'Iteration 3' },
	{ id: 4, label: 'IT4', fullLabel: 'Iteration 4' },
]

const RelistModal = ({ isOpen = false, auction = null, onClose, onSuccess }) =>
{
	const [isModalBusy, setIsModalBusy] = useState(false)
	const { t } = useTranslation()
	const sessionContext = useContext(SessionContext)
	const [step, setStep] = useState(1) // Step 1: Confirm relist, Step 2: Select iteration
	const [selectedIterationId, setSelectedIterationId] = useState('')

	const handleCancelModal = () =>
	{
		resetForm()
		if (typeof onClose === 'function')
		{
			onClose()
		}
	}

	const resetForm = () =>
	{
		setStep(1)
		setSelectedIterationId('')
	}

	const handleNextStep = () =>
	{
		// Step 1 just confirms they want to relist, move to step 2
		setStep(2)
	}

	const handleConfirmRelist = () =>
	{
		if (!selectedIterationId)
		{
			trigger('raiseNotification', { title: 'Validation Error', message: 'Please select a target iteration.', type: 'warning', duration: 5 })
			return
		}

		setIsModalBusy(true)

		AuctionTabsService
			.relistAuction(
				auction.auctionId, 
				parseInt(selectedIterationId), 
				null, // Keep current start price
				null, // Keep current reserve price
				null, // Keep current buy now price
				'', // No notes in simplified UI
				sessionContext.loginSession.user.userSession.sessionKey
			)
			.then(result =>
			{
				setIsModalBusy(false)
				sessionContext.updateSession(result.data.loginSession)

				const iterationLabel = ITERATION_OPTIONS.find(i => i.id === parseInt(selectedIterationId))?.label || 'selected iteration'
				trigger('raiseNotification', { 
					title: 'Auction Relisted', 
					message: `Vehicle has been queued for release to ${iterationLabel}. The auction will go live shortly.`, 
					type: 'success', 
					duration: 5 
				})
				resetForm()
				if (typeof onSuccess === 'function')
				{
					onSuccess()
				}
			})
			.catch((error) =>
			{
				setIsModalBusy(false)
				SiteService.displayApiError(error)
			})
	}

	const vehicleDescription = auction?.vehicleDescription || 
		`${auction?.year || auction?.modelYear || ''} ${auction?.make || ''} ${auction?.model || ''}`.trim() || 
		'Selected Vehicle'

	const currentIteration = auction?.currentIteration || auction?.iterationName || 'Unknown'
	const selectedIteration = ITERATION_OPTIONS.find(i => i.id === parseInt(selectedIterationId))

	return (
		<Modal
			className="modal-dialog-centered"
			size="md"
			isOpen={isOpen}
		>
			<SpinnerComponent
				loading={isModalBusy}
				position="centered"
				message="Relisting auction..."
			/>
			<Card className="mb-0">
				<CardBody>
					{/* BRD: Step 1 - "Are you sure you want to relist this unit?" */}
					{step === 1 && (
						<>
							<div className="text-center mb-4">
								<h5>Relist VIN: {auction?.vin || 'Unknown'}</h5>
								<p className="text-muted mb-0">Are you sure you want to relist this unit?</p>
							</div>

							<Row className="mt-4">
								<Col className="d-flex justify-content-center gap-3">
									<Button color="primary" onClick={handleNextStep}>
										YES
									</Button>
									<Button color="secondary" onClick={handleCancelModal}>
										Cancel
									</Button>
								</Col>
							</Row>
						</>
					)}

					{/* BRD: Step 2 - "Which iteration:" with MBP, IT2, IT3, IT4 buttons */}
					{step === 2 && (
						<>
							<div className="text-center mb-4">
								<h5>Relist VIN: {auction?.vin || 'Unknown'}</h5>
								<p className="text-muted mb-0">Which iteration:</p>
							</div>

							{/* BRD: Iteration selection buttons */}
							<div className="d-flex justify-content-center gap-2 mb-4">
								{ITERATION_OPTIONS.map((iteration) => (
									<Button
										key={iteration.id}
										color={selectedIterationId === iteration.id.toString() ? 'primary' : 'outline-secondary'}
										onClick={() => setSelectedIterationId(iteration.id.toString())}
										style={{ minWidth: '60px' }}
									>
										{iteration.label}
									</Button>
								))}
							</div>

							<Row className="mt-4">
								<Col className="d-flex justify-content-center">
									<Button 
										color="primary" 
										onClick={handleConfirmRelist} 
										disabled={!selectedIterationId}
										style={{ minWidth: '120px' }}
									>
										Confirm
									</Button>
								</Col>
							</Row>
						</>
					)}
				</CardBody>
			</Card>
		</Modal>
	)
}

export default RelistModal
